/**
 * VAST tag builder (PRD §13 "VAST tag the player builds", C1, C15, C19, C22, C23, C28, §2.1, §12.2).
 * Every runtime value is set from the real state at request time; nothing policy-relevant is hard-coded
 * to anything other than the locked values.
 */
import { GAM_VAST_BASE, LOCKED } from './config.js';

/**
 * Random correlator, unique per request.
 * @param {Function} random Math.random-like
 * @returns {string}
 */
export function makeCorrelator(random = Math.random) {
  return String(Math.floor(random() * 1e15));
}

/**
 * Encode custom targeting as GAM expects: an inner query string, encoded again by the outer URL.
 * @param {object} custParams key/value pairs; empty values are dropped
 * @returns {string}
 */
export function encodeCustParams(custParams) {
  const inner = new URLSearchParams();
  for (const key of Object.keys(custParams || {})) {
    const value = custParams[key];
    if (value === undefined || value === null || value === '') continue;
    inner.set(key, String(value));
  }
  return inner.toString();
}

/**
 * Build the GAM VAST request URL for one pre-roll break.
 * Throws when description_url is missing (C28: no video request without it).
 * @param {object} input
 * @param {string} input.adUnit e.g. /NETWORK/XX_Video
 * @param {string} input.sizes ad unit sizes, kept as declared (§7.2)
 * @param {boolean} input.muted real audio state at request time (C1)
 * @param {'auto'|'click'} input.startType how this clip was started (C19)
 * @param {boolean} input.autoAdvance auto-advance on (C19 vconp)
 * @param {number} input.maxAdDurationMs from the ad budget, never above 30000 (C18, C23)
 * @param {string} input.pageUrl page URL
 * @param {string} input.descriptionUrl resolved description_url (C28)
 * @param {string} input.cmsid content source id (§12.2)
 * @param {string} input.clipId vid
 * @param {number} input.clipSeconds vid_d
 * @param {object} input.custParams site, qs_src, qs_cmp, player, vcat
 * @param {import('./consent.js').ConsentSignals} input.consent
 * @param {boolean} [input.adTest] adtest=on for internal traffic (§2.2)
 * @param {string} input.correlator
 * @param {string} [input.baseUrl] Prebid-built GAM URL to extend (§12.3)
 * @returns {string}
 */
export function buildVastUrl(input) {
  if (!input.descriptionUrl) throw new Error('description_url_missing'); // C28
  const url = new URL(input.baseUrl || GAM_VAST_BASE);
  const params = url.searchParams;
  const prebidCust = params.get('cust_params');

  params.set('iu', input.adUnit);
  params.set('sz', input.sizes); // §7.2: declared sizes; real size goes to IMA
  params.set('env', 'vp');
  params.set('gdfp_req', '1');
  params.set('output', 'vast');
  params.set('unviewed_position_start', '1');
  params.set('plcmt', String(LOCKED.plcmt)); // C1: locked to 2, never 1
  params.set('vpmute', input.muted ? '1' : '0'); // C1: real state at request time
  params.set('vpa', input.startType === 'click' ? 'click' : 'auto'); // C19
  params.set('vpos', 'preroll'); // C15: pre-rolls only, no mid- or post-rolls
  params.set('vad_type', 'linear'); // C22: linear only
  params.set('ad_rule', '0'); // §12.1: single pre-roll requests, no ad rules
  const maxAd = Math.min(LOCKED.maxAdDurationMs, Math.max(0, Math.floor(input.maxAdDurationMs || 0)));
  params.set('max_ad_duration', String(maxAd)); // C18, C23
  params.set('url', input.pageUrl || input.descriptionUrl);
  params.set('description_url', input.descriptionUrl); // C28 (URLSearchParams encodes it)
  params.set('correlator', input.correlator);
  if (input.cmsid) params.set('cmsid', input.cmsid); // §12.2
  if (input.clipId) params.set('vid', input.clipId);
  if (input.clipSeconds) params.set('vid_d', String(Math.round(input.clipSeconds)));
  params.set('vconp', input.autoAdvance ? '2' : '1'); // C19 / §12.2
  params.set('hl', 'en');

  const ours = encodeCustParams(input.custParams);
  params.set('cust_params', prebidCust ? prebidCust + (ours ? '&' + ours : '') : ours);

  const consent = input.consent || {};
  if (consent.gdpr === 0 || consent.gdpr === 1) params.set('gdpr', String(consent.gdpr));
  if (consent.gdprConsent) params.set('gdpr_consent', consent.gdprConsent);
  if (consent.addtlConsent) params.set('addtl_consent', consent.addtlConsent);
  if (consent.gpp) params.set('gpp', consent.gpp);
  if (consent.gppSid) params.set('gpp_sid', consent.gppSid);
  params.set('npa', consent.npa ? '1' : '0');
  params.set('ltd', consent.ltd ? '1' : '0');
  params.set('rdp', consent.rdp ? '1' : '0');
  params.set('tfcd', consent.tfcd ? '1' : '0');
  if (input.adTest) params.set('adtest', 'on');
  else params.delete('adtest');
  return url.toString();
}
